import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { PasswordInput } from "@/components/PasswordInput";
import { Button } from "@/components/Button";
import { authApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { validatePassword } from "@/utils/passwordPolicy";
import { Icon } from "@/components/Icon";
import { Logo } from "@/components/Logo";

export const ForcePasswordChange = () => {
    const { t } = useTranslation("global");
    const { user, logout } = useAuth();
    const { showSuccess, showError } = useToast();

    const [password, setPassword] = useState("");
    const [passwordConfirm, setPasswordConfirm] = useState("");
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password !== passwordConfirm) {
            showError(t("users.passwords_do_not_match"));
            return;
        }

        const passwordErrorKey = validatePassword(password);
        if (passwordErrorKey) {
            showError(t(passwordErrorKey));
            return;
        }

        setLoading(true);
        try {
            await authApi.updatePassword({
                password: password,
                password_confirmation: passwordConfirm,
            });
            showSuccess(t("users.password_updated_successfully"));
            window.location.reload();
        } catch (error) {
            showError(error.message || t("users.error_update"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-page p-4">
            <div className="max-w-md w-full bg-white dark:bg-zinc-900 rounded-2xl shadow-xl border border-slate-200/60 dark:border-zinc-800/60 overflow-hidden">
                <div className="p-8">
                    <div className="flex justify-center mb-6">
                        <Logo className="h-12 w-auto" />
                    </div>

                    <h2 className="text-2xl font-bold text-center text-slate-800 dark:text-zinc-100 mb-2">
                        {t("users.force_password_change_title")}
                    </h2>

                    <p className="text-center text-sm text-slate-500 dark:text-zinc-400 mb-8">
                        {t("users.force_password_change_desc")}
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <PasswordInput
                            label={t("users.new_password")}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />

                        <PasswordInput
                            label={t("users.confirm_password")}
                            value={passwordConfirm}
                            onChange={(e) => setPasswordConfirm(e.target.value)}
                        />

                        <Button
                            type="submit"
                            variant="primary"
                            className="w-full justify-center mt-2"
                            loading={loading}
                        >
                            {t("users.update_password")}
                        </Button>
                    </form>

                    <div className="mt-6 text-center">
                        <Button
                            variant="danger"
                            icon={<Icon value="power" size={16} />}
                            onClick={() => {
                                logout();
                            }}
                        >
                            {t("auth.logout")}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};
